import { PureComponent, ReactNode } from 'react';
import { PAD_V2_CONTAINER } from '../PadContainer';
import BottomActionStrip from './CareCanvasBottomActionStrip';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../../store/reducers';
import {
    getCareCanvas,
    getPatientPersonalProfile,
    getPrescription,
    getRxDoctor,
} from '../../../../../store/selectors/prescriptionPad';
import { Doctor, PatientPersonalProfile } from '../../../../../store/types/prescription';
import { getHeaderAndFooterContent } from '../../../leftPanel/DentalChart/Utils';
import { getAgeString } from '../../../../../store/utils';
import { ORBIT } from '../../../../../api/constants';
import {
    canvasIcon,
    imageIcon,
    pageIcon,
    refreshIcon,
    templateIcon,
} from '../../../../common/Icons';
import { track } from '../../../../../tracking';
import { imageToBase64, selectElementAndClick, uploadImage } from './utils';
import { notification } from 'antd';
import { CareCanvas, CareCanvasTemplate } from '../../../../../store/types/CareCanvas';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../../store/actions';
import getCareCanvasTemplate from '../../../../../api/getCareCanvasTemplate';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import uploadCareCanvasTemplate from '../../../../../api/uploadCareCanvasTemplate';
import { upsertCareCanvas } from '../../../../../store/actions/prescriptionPad';
import { GO_TO_PRESCRIPTION_PAD } from '../../../../../routes/paths';
import { his } from '../../../../../pages/history';
import BlockHistory from './BlockHistory';
import PreviewTemplateModal from './PreviewTemplate';
import { WarningModal } from './WarningModal';
import { getAddPDForms } from '../../../../../pages/utils';

const ANNOTATABLE_IMAGE = 'annotatable-image';
const IMAGE_ANNOTATION_FILE_INPUT_ID = 'image-annotation-file-input-id';
export const DEFAULT_IMAGE = '/images/blank_white_img.png';
const MARKER_JS_2_SCRIPT_TAG_ID = 'marker-js-2-script';

enum MARKER_JS_SCRIPT_LOAD_STATUS {
    LOADING = 'loading',
    SUCCESS = 'success',
    ERROR = 'error',
}

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    prescriptionId: string;
    patientPersonalProfile?: PatientPersonalProfile;
    docid: string;
    docProfile?: Doctor;
    careCanvas?: CareCanvas[];
}

interface State {
    uploadedImage?: string;
    markerAreaState?: any;
    isAnnotating?: boolean;
    markerJsScriptLoadStatus?: MARKER_JS_SCRIPT_LOAD_STATUS;
    isPrintingCanvas?: boolean;
    isSavingCanvas?: boolean;
    isFetchingCanvas?: boolean;
    canvasName?: string;
    isStateChanged?: boolean;
    previewTemplate?: CareCanvasTemplate;
    useTemplateVisible?: boolean;
}

class PPCareCanvas extends PureComponent<Props, State> {
    state: State = {};

    componentDidMount = () => {
        const { loadScript } = this;
        loadScript();
        track('PP_CARE_CANVAS_RENDER');
    };

    componentWillUnmount = () => {
        const script = document.getElementById(MARKER_JS_2_SCRIPT_TAG_ID);
        script && script.remove();
        const markerJsContainer = document.querySelector('.__markerjs2_');
        markerJsContainer && markerJsContainer.remove();
    };

    loadScript = () => {
        this.setState({
            markerJsScriptLoadStatus: MARKER_JS_SCRIPT_LOAD_STATUS.LOADING,
            isFetchingCanvas: true,
        });
        const script = document.createElement('script');
        script.src = '/markerjs2.js';
        script.async = true;
        script.id = MARKER_JS_2_SCRIPT_TAG_ID;
        script.onload = () => {
            const newState = { markerJsScriptLoadStatus: MARKER_JS_SCRIPT_LOAD_STATUS.SUCCESS };
            this.setState(newState, this.fetchCanvasDetails);
        };
        script.onerror = (e) => {
            track('MARKER_JS_SCRIPT_LOAD_FAILED');
            this.setState({ markerJsScriptLoadStatus: MARKER_JS_SCRIPT_LOAD_STATUS.ERROR });
        };
        document.body.appendChild(script);
    };

    fetchCanvasDetails = async () => {
        const { careCanvas } = this.props;
        if (!careCanvas || careCanvas.length === 0) {
            this.setState({
                isFetchingCanvas: false,
            });
            return;
        }
        try {
            const { template } = await getCareCanvasTemplate(careCanvas[0].id);
            if (template) {
                this.onApplyTemplate(template);
            } else {
                this.setState({
                    isFetchingCanvas: false,
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to get canvas template',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'fetchCanvasDetails', what: (error as Error).message });
            this.setState({
                isFetchingCanvas: false,
            });
        }
    };

    onUploadClick = () => {
        selectElementAndClick(`#${IMAGE_ANNOTATION_FILE_INPUT_ID}`);
        track('ON_UPLOAD_IMAGE_CLICK');
    };

    onUploadImage = () => {
        const { uploadedImage } = this.state;
        const { onUploadClick } = this;
        const mainText = 'Are you sure you want to upload a new one?';
        const subText = 'An image is already applied. The current one will be replaced.';
        const onContinue = async () => onUploadClick();
        if (uploadedImage) {
            WarningModal(mainText, subText, onContinue);
        } else {
            onUploadClick();
        }
    };

    onImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { onShowMarkerArea } = this;
        const file = event.target.files?.[0];
        if (file) {
            let { name } = file;
            name = name.substring(0, name.lastIndexOf('.'));
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result) {
                    const image = reader.result as string;
                    const newState = {
                        uploadedImage: image,
                        markerAreaState: undefined,
                        canvasName: name,
                    };
                    this.setState(newState, onShowMarkerArea);
                }
            };
            reader.readAsDataURL(file);
        }
        track('ON_IMAGE_UPLOAD');
    };

    onClear = () => {
        const { dispatch } = this.props;
        this.setState({
            uploadedImage: undefined,
            markerAreaState: undefined,
            canvasName: undefined,
        });
        dispatch(upsertCareCanvas([]));
        track('ON_CLEAR_CANVAS');
    };

    onBlankCanvas = () => {
        const { onShowMarkerArea } = this;
        this.setState({
            uploadedImage: `${DEFAULT_IMAGE}?t=${Math.random()}`,
            canvasName: 'Canvas',
        });
        setTimeout(onShowMarkerArea, 100);
        track('ON_BLANK_CANVAS');
    };

    onShowMarkerArea = () => {
        const { markerAreaState, uploadedImage } = this.state;
        const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
        if (!annotatableImage || !uploadedImage) return;
        const prevSrc = annotatableImage.src;
        annotatableImage.src = uploadedImage;
        this.setState({ isAnnotating: true });
        const markerArea = new (window as any).markerjs2.MarkerArea(annotatableImage);
        markerArea.zoomSteps = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        markerArea.uiStyleSettings.zoomButtonVisible = true;
        markerArea.uiStyleSettings.zoomOutButtonVisible = true;
        markerArea.uiStyleSettings.clearButtonVisible = true;
        markerArea.uiStyleSettings.undoButtonVisible = true;
        markerArea.uiStyleSettings.redoButtonVisible = true;
        markerArea.availableMarkerTypes = markerArea.ALL_MARKER_TYPES;
        if (!markerAreaState) {
            markerArea.addEventListener('show', (event: any) => {
                setTimeout(() => {
                    event.markerArea.createNewMarker((window as any).markerjs2.FreehandMarker);
                }, 200);
            });
        }
        markerArea.addEventListener('restorestate', (event: any) => {
            setTimeout(() => {
                event.markerArea.createNewMarker((window as any).markerjs2.FreehandMarker);
            }, 200);
        });
        markerArea.show();
        if (markerAreaState) {
            setTimeout(() => markerArea.restoreState(markerAreaState));
        }
        let stateChanged = false;
        markerArea.addEventListener('render', (event: any) => {
            annotatableImage.src = event.dataUrl;
            stateChanged = true;
            this.setState({
                markerAreaState: event.state,
                isStateChanged: true,
            });
        });
        markerArea.addEventListener('close', () => {
            if (!stateChanged) {
                annotatableImage.src = prevSrc;
            }
            this.setState({ isAnnotating: false });
        });
        track('ON_SHOW_MARKER_AREA');
    };

    onPrint = async () => {
        const { prescriptionId, patientPersonalProfile, docProfile } = this.props;
        if (!docProfile) {
            return;
        }
        const ptFormDets = await getAddPDForms();
        const printWindow = document.createElement('iframe');
        const { headerHtml, footerHtml } = getHeaderAndFooterContent(
            docProfile,
            prescriptionId,
            ptFormDets,
        );
        const patientDetails = [
            patientPersonalProfile?.gender,
            getAgeString(
                {
                    age: patientPersonalProfile?.age?.value,
                    month: patientPersonalProfile?.age?.month,
                    dob: patientPersonalProfile?.age?.dob,
                },
                ' ',
            ),
            `${patientPersonalProfile?.phone?.c || ''}${patientPersonalProfile?.phone?.n || ''}`,
        ].filter(Boolean);

        const pdData = patientDetails.join(', ');
        const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
        this.setState({
            isPrintingCanvas: true,
        });
        printWindow.srcdoc = `
            <!DOCTYPE html>
            <html lang="en">
                <head>
                    <title>Print</title>
                    <link href=${ORBIT} rel="stylesheet">
                    <style>
                       .selected-tooth {
                            border: 1px solid #215FFF;
                        }
                    </style>
                </head>
                <body>
                    <div class="h-full w-full flex flex-col">
                        ${headerHtml}
                        <div class="flex justify-between pt-8">
                            <div class="text-13">
                                <span class="text-13 bold">${
                                    patientPersonalProfile?.name || ''
                                }, </span>
                                                ${pdData}
                            </div>
                        </div>
                        <div class="flex-1 space-y-12 overflow-hidden">
                            <img src="${
                                annotatableImage?.src
                            }" style="max-width: 100%; max-height: 100%; margin: 12px auto;" />
                        </div>
                        ${footerHtml}
                    </div>
                </body>
            </html>
        `;
        document.body.appendChild(printWindow);
        printWindow.onload = () => {
            const pW = printWindow.contentWindow;
            if (!pW) return;
            pW.focus();
            pW.print();
            pW.onafterprint = () => {
                document.body.removeChild(printWindow);
            };
            this.setState({ isPrintingCanvas: false });
        };
        track('ON_PRINT_CANVAS');
    };

    onApplyTemplate = async (template: CareCanvasTemplate) => {
        const { onShowMarkerArea } = this;
        const { uploaded_image, final_image, marker_area_state } = template;
        const uploadedImageDataUrl = await imageToBase64(uploaded_image);
        const finalImageDataUrl = await imageToBase64(final_image);
        if (!uploadedImageDataUrl || !finalImageDataUrl) {
            notification.error({
                message: 'Failed to apply template',
                duration: 5,
            });
            this.setState({ isFetchingCanvas: false });
            return;
        }
        const newState = {
            uploadedImage: uploadedImageDataUrl,
            markerAreaState: marker_area_state,
            isFetchingCanvas: false,
            canvasName: template.name,
        };
        const callback = () => {
            const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
            if (annotatableImage) {
                annotatableImage.src = finalImageDataUrl;
            }
            onShowMarkerArea();
        };
        this.setState(newState, callback);
        track('ON_APPLY_TEMPLATE', { templateId: template._id });
    };

    onSave = async () => {
        const { prescriptionId, dispatch, docid } = this.props;
        const { uploadedImage, markerAreaState, canvasName = 'Canvas' } = this.state;
        const { goToPrescriptionPad } = this;
        const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
        const finalImage = annotatableImage?.src;
        if (!uploadedImage || !finalImage) {
            return;
        }
        this.setState({
            isSavingCanvas: true,
        });
        const id = `care-canvas-${prescriptionId.toLocaleLowerCase()}`;
        const uploadedImageDataUrl = uploadedImage.includes(DEFAULT_IMAGE)
            ? await imageToBase64(uploadedImage)
            : uploadedImage;
        const finalImageDataUrl = finalImage.includes(DEFAULT_IMAGE)
            ? await imageToBase64(finalImage)
            : finalImage;
        if (!uploadedImageDataUrl || !finalImageDataUrl) {
            notification.error({
                message: 'Failed to save template',
                duration: 5,
            });
            this.setState({
                isSavingCanvas: false,
            });
            return;
        }
        const uploadedImageUrl = await uploadImage(uploadedImageDataUrl, `base-image-${id}`);
        const finalImageUrl = await uploadImage(finalImageDataUrl, `final-image-${id}`);
        if (!uploadedImageUrl || !finalImageUrl) {
            notification.error({
                message: 'Failed to upload images',
                duration: 5,
            });
            this.setState({
                isSavingCanvas: false,
            });
            return;
        }
        const careCanvasTemplate: CareCanvasTemplate = {
            _id: id,
            docid: docid,
            name: canvasName,
            uploaded_image: uploadedImageUrl,
            final_image: finalImageUrl,
            marker_area_state: markerAreaState,
            created_at: new Date().toISOString(),
            type: 'prescription',
        };
        try {
            const { success, error } = await uploadCareCanvasTemplate(careCanvasTemplate);
            if (success) {
                const careCanvas = [{ id: id, name: canvasName, final_image: finalImageUrl }];
                dispatch(upsertCareCanvas(careCanvas));
                notification.success({
                    message: 'Canvas has been successfully added to Rx',
                    duration: 5,
                });
                goToPrescriptionPad();
            } else {
                notification.error({
                    message: 'Failed to save canvas',
                    duration: 5,
                });
                captureErrorString(error || 'Failed to save canvas');
                track('ERROR', { on: 'uploadCareCanvas', what: error || 'Failed to save canvas' });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save canvas',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'uploadCareCanvasTemplate', what: (error as Error).message });
        } finally {
            this.setState({
                isSavingCanvas: false,
            });
        }
        track('ON_SAVE_CANVAS');
    };

    goToPrescriptionPad = () => {
        const { prescriptionId } = this.props;
        if (!prescriptionId) {
            his.replace('/');
            return;
        }
        his.push(GO_TO_PRESCRIPTION_PAD(prescriptionId, 'edit'));
        track('GO_TO_PRESCRIPTION_PAD');
    };

    onContinue = (): void => {
        const { uploadedImage } = this.state;
        const { goToPrescriptionPad, onSave } = this;
        const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
        const finalImage = annotatableImage?.src;
        if (!uploadedImage || !finalImage) {
            goToPrescriptionPad();
            return;
        }
        onSave();
    };

    onSaveWithStateSave = async () => {
        const { onSave } = this;
        selectElementAndClick('div[data-action="render"]');
        await new Promise((resolve) => setTimeout(resolve, 100));
        await onSave();
    };

    applyTemplate = (template: CareCanvasTemplate) => {
        const { dispatch } = this.props;
        const { onApplyTemplate } = this;
        onApplyTemplate(template);
        const { _id, name, final_image } = template;
        const careCanvas = [{ id: _id, name, final_image }];
        dispatch(upsertCareCanvas(careCanvas));
    };

    onApplyTemplateClick = (template: CareCanvasTemplate) => {
        const { uploadedImage } = this.state;
        const { applyTemplate } = this;
        const mainText = 'Are you sure you want to apply this template?';
        const subText = 'An image is already applied. The current one will be replaced.';
        const onContinue = async () => applyTemplate(template);
        if (uploadedImage) {
            WarningModal(mainText, subText, onContinue);
        } else {
            applyTemplate(template);
        }
    };

    setPreviewTemplate = (template?: CareCanvasTemplate) => {
        this.setState({ previewTemplate: template });
    };

    setUseTemplateVisible = (visible: boolean) => {
        this.setState({ useTemplateVisible: visible });
    };

    render(): ReactNode {
        const { docid } = this.props;
        const {
            uploadedImage,
            isAnnotating,
            markerAreaState,
            markerJsScriptLoadStatus,
            isPrintingCanvas,
            isSavingCanvas,
            isFetchingCanvas,
            isStateChanged,
            previewTemplate,
            useTemplateVisible,
        } = this.state;
        const {
            onUploadImage,
            onImageUpload,
            onClear,
            onShowMarkerArea,
            onPrint,
            onBlankCanvas,
            onContinue,
            onSaveWithStateSave,
            onApplyTemplateClick,
            setPreviewTemplate,
            setUseTemplateVisible,
        } = this;
        const hideStrip =
            isAnnotating || markerJsScriptLoadStatus !== MARKER_JS_SCRIPT_LOAD_STATUS.SUCCESS;
        const annotatableImage = document.getElementById(ANNOTATABLE_IMAGE) as HTMLImageElement;
        const finalImage = annotatableImage?.src;

        return (
            <div
                className="p-16 w-full h-full flex flex-col space-y-12 overflow-hidden"
                id={PAD_V2_CONTAINER}
            >
                {(isStateChanged || isAnnotating) && uploadedImage && finalImage && (
                    <BlockHistory onSave={onSaveWithStateSave} />
                )}
                {markerJsScriptLoadStatus === MARKER_JS_SCRIPT_LOAD_STATUS.SUCCESS ? (
                    isFetchingCanvas ? (
                        <div
                            className="flex items-center justify-center flex-1 overflow-hidden rounded-8 bg-white mt-28"
                            style={{
                                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                borderColor: '#E8E8E8',
                                borderBottomWidth: '0px',
                            }}
                        >
                            <svg
                                className="w-28 h-28 fill-darwin-accent-bright-blue-800 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        </div>
                    ) : (
                        <div
                            className={`flex items-center justify-center flex-1 overflow-auto rounded-8 bg-white mt-28 ${
                                uploadedImage
                                    ? 'transition-all duration-300 ease-in-out annotatable-image-container-hover cursor-pointer'
                                    : ''
                            }`}
                            style={{
                                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                borderColor: '#E8E8E8',
                                borderBottomWidth: '0px',
                            }}
                            onClick={uploadedImage ? onShowMarkerArea : undefined}
                        >
                            {uploadedImage ? (
                                <img
                                    id={ANNOTATABLE_IMAGE}
                                    src={uploadedImage}
                                    className={`${
                                        uploadedImage.includes(DEFAULT_IMAGE)
                                            ? 'w-full h-full'
                                            : 'm-auto max-h-full'
                                    } bg-white ${isAnnotating ? 'invisible' : 'visible'}`}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <div
                                        style={{
                                            borderColor: ' #BABABA',
                                        }}
                                        className="w-300 p-32 flex flex-col items-center justify-center space-y-18 border border-dashed rounded-8"
                                    >
                                        <p>{canvasIcon('w-28 h-28 fill-darwin-neutrals-600')}</p>
                                        <div className="flex flex-col space-y-18">
                                            <button
                                                className="px-12 py-10 flex items-center space-x-8 bg-darwin-accent-bright-blue-800 rounded-8"
                                                onClick={onBlankCanvas}
                                            >
                                                <p className="text-13 font-700 text-white">
                                                    New blank canvas
                                                </p>
                                                {pageIcon('w-10 h-14 fill-white relative top-1')}
                                            </button>
                                            <button
                                                className="px-11 py-9 flex items-center justify-center space-x-8 border border-darwin-accent-bright-blue-800 rounded-8"
                                                onClick={onUploadImage}
                                            >
                                                <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                                    Upload image
                                                </p>
                                                {imageIcon(
                                                    'w-14 h-18 fill-darwin-accent-bright-blue-800 relative top-1',
                                                )}
                                            </button>
                                            <button
                                                className="px-11 py-9 flex items-center justify-center space-x-8 border border-darwin-accent-bright-blue-800 rounded-8"
                                                onClick={() => setUseTemplateVisible(true)}
                                            >
                                                <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                                    Use template
                                                </p>
                                                {templateIcon(
                                                    'w-14 h-14 fill-darwin-accent-bright-blue-800 relative top-1',
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                ) : (
                    <div
                        className="flex items-center justify-center flex-1 overflow-hidden rounded-8 bg-white mt-28"
                        style={{
                            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                            borderColor: '#E8E8E8',
                            borderBottomWidth: '0px',
                        }}
                    >
                        {markerJsScriptLoadStatus === MARKER_JS_SCRIPT_LOAD_STATUS.ERROR ? (
                            <button
                                className="px-11 py-9 flex items-center space-x-8 border border-red-primary rounded-8"
                                onClick={this.loadScript}
                            >
                                <p className="text-13 font-700 text-red-primary">Refresh</p>
                                {refreshIcon('w-12 h-12 fill-red-primary relative top-1')}
                            </button>
                        ) : (
                            <svg
                                className="w-28 h-28 fill-darwin-accent-bright-blue-800 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        )}
                    </div>
                )}
                <BottomActionStrip
                    onUpload={onUploadImage}
                    onClear={onClear}
                    onPrint={onPrint}
                    hideStrip={hideStrip}
                    isSaveDisabled={!uploadedImage || !finalImage}
                    uploadedImage={uploadedImage}
                    finalImage={finalImage}
                    markerAreaState={markerAreaState}
                    docid={docid}
                    isPrintingCanvas={isPrintingCanvas}
                    onContinue={onContinue}
                    isSavingCanvas={isSavingCanvas}
                    applyTemplate={onApplyTemplateClick}
                    setPreviewTemplate={setPreviewTemplate}
                    useTemplateVisible={useTemplateVisible}
                    setUseTemplateVisible={setUseTemplateVisible}
                />
                <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={onImageUpload}
                    className="hidden"
                    id={IMAGE_ANNOTATION_FILE_INPUT_ID}
                />
                <PreviewTemplateModal
                    closeMe={() => setPreviewTemplate(undefined)}
                    template={previewTemplate}
                    applyTemplate={onApplyTemplateClick}
                />
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    prescriptionId: getPrescription(state)?.id || '',
    patientPersonalProfile: getPatientPersonalProfile(state),
    docid: getPrescription(state)?.doctor?._id || '',
    docProfile: getRxDoctor(state),
    careCanvas: getCareCanvas(state),
}))(PPCareCanvas);
